import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from '../style/containers/BadgeSharePage.module.scss';

// Mock badge data - can be replaced with API calls later
const MOCK_BADGES = {
  1: {
    name: 'Consistency Champion',
    description: 'Completed 5 Language Practice sessions',
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/2583/2583344.png',
    achievementDate: '2025-05-10T14:30:00Z',
    badgeType: 'Achievement',
    totalCount: 5
  },
  2: {
    name: 'Perfect Week',
    description: 'Studied every day for a week',
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/3112/3112946.png',
    achievementDate: '2025-05-08T10:15:00Z',
    badgeType: 'Streak',
    totalCount: 7
  },
  3: {
    name: 'Word Master',
    description: 'Learned 100 new vocabulary words',
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/4359/4359732.png',
    achievementDate: '2025-05-05T09:45:00Z',
    badgeType: 'Vocabulary',
    totalCount: 100
  }
};

/**
 * Public badge share page component that doesn't require authentication
 * Uses mock data initially, but can be updated to use API data
 */
const BadgeSharePage = () => {
  const { badgeId } = useParams();
  const [badge, setBadge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set meta tags for social sharing
  useEffect(() => {
    if (badge) {
      const { name, description, iconUrl } = badge;

      // Format page title and description for sharing
      const pageTitle = `${name} Badge Achievement on Chatterbox`;
      const pageDescription = description || `I earned the ${name} badge for language learning progress!`;

      // Set the document title
      document.title = pageTitle;

      // Set meta tags for social sharing
      const metaTags = {
        description: pageDescription,
        'og:title': pageTitle,
        'og:description': pageDescription,
        'og:image': iconUrl,
        'og:image:width': '1200',
        'og:image:height': '630',
        'og:image:alt': `${name} achievement badge`,
        'og:url': window.location.href,
        'og:type': 'website',
        'og:site_name': 'Chatterbox Language Learning',
        'twitter:card': 'summary_large_image',
        'twitter:title': pageTitle,
        'twitter:description': pageDescription,
        'twitter:image': iconUrl
      };

      // Clear any existing meta tags
      document.querySelectorAll('meta').forEach((meta) => {
        if (meta.getAttribute('name') !== 'viewport' && meta.getAttribute('charset') !== 'utf-8') {
          meta.parentNode.removeChild(meta);
        }
      });

      // Add new meta tags
      Object.entries(metaTags).forEach(([name, content]) => {
        const meta = document.createElement('meta');
        if (name.startsWith('og:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      });

      // Add canonical link
      const canonicalLink = document.querySelector('link[rel="canonical"]') || document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', window.location.href);
      document.head.appendChild(canonicalLink);
    }
  }, [badge]);

  // Load badge data on mount
  useEffect(() => {
    // Simulate API call with setTimeout
    setLoading(true);

    setTimeout(() => {
      if (MOCK_BADGES[badgeId]) {
        setBadge(MOCK_BADGES[badgeId]);
        setLoading(false);
      } else {
        setError('Badge not found');
        setLoading(false);
      }
    }, 500); // Simulate network delay

    // In the future, replace with actual API call:
    // axios.get(`/api/badges/public/${badgeId}`)
    //   .then(response => {
    //     setBadge(response.data);
    //     setLoading(false);
    //   })
    //   .catch(err => {
    //     setError('Badge not found');
    //     setLoading(false);
    //   });
  }, [badgeId]);

  // Show loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}></div>
        <p>Loading badge details...</p>
      </div>
    );
  }

  // Show error state
  if (error || !badge) {
    return (
      <div className={styles.errorContainer}>
        <h1>Badge Not Found</h1>
        <p>{error || 'The requested badge could not be found.'}</p>
        <a href="/" className={styles.homeLink}>
          Go to Homepage
        </a>
      </div>
    );
  }

  const { name, description, iconUrl, achievementDate, badgeType } = badge;

  // Format achievement date if available
  const formattedDate = achievementDate
    ? new Date(achievementDate).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : null;

  return (
    <div className={styles.pageWrapper}>
      <main className={styles.badgeSharePage}>
        <div className={styles.badgeContainer}>
          <h1>Badge Achievement</h1>

          <div className={styles.badgeDetails}>
            <img src={iconUrl} alt={`${name} badge`} className={styles.badgeImage} />

            <h2>{name}</h2>

            <p className={styles.badgeDescription}>{description}</p>

            {badgeType && (
              <div className={styles.badgeType}>
                <span>{badgeType}</span>
              </div>
            )}

            {formattedDate && <p className={styles.achievementDate}>Earned on {formattedDate}</p>}
          </div>

          <div className={styles.ctaSection}>
            <a href="https://chatterbox.io" className={styles.tryButton}>
              Learn More About Chatterbox Languages
            </a>
          </div>
        </div>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.copyright}>&copy; {new Date().getFullYear()} Chatterbox. All rights reserved.</div>
          <div className={styles.links}>
            <a href="https://www.chatterbox.io/privacy-policy">Privacy Policy</a>
            <a href="https://www.chatterbox.io/cookie-policy">Cookie Policy</a>
            <a href="https://www.chatterbox.io/terms-cons">Terms & Conditions</a>
            <a href="mailto:hello@chatterbox.io">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BadgeSharePage;
